import { useCallback, useSyncExternalStore } from 'react';
import { useStore } from 'react-redux';

import { type AppState } from '../../reducer';
import { type Settings } from '../../settings';

/**
 * Hook to access settings from Redux state.
 * Settings are managed by LaunchDarkly and should remain in Redux.
 *
 * BIS-3204: reads go through useSyncExternalStore rather than useSelector.
 * react-redux 7.2.2 predates concurrent rendering and can drop store
 * notifications under React 19. Settings co-gate first render in Root
 * (`!isUserReady || isFetching`): UPDATE_SETTINGS_PRIVATE sets isFetching
 * true, and if the FULFILLED that clears it lands mid-mount and the
 * notification is dropped, Root renders <AppLoading /> forever off a stale
 * `true` while the store reads `false`. This is the settings-slice twin of
 * the auth fix in WithAuth/useAuth — the faster the boot (warm caches, quick
 * API), the more likely the dispatch lands inside the drop window. Collapse
 * back to useSelector once react-redux is on v9, which is built on this same
 * primitive.
 */
export function useSettings(): Settings {
  const store = useStore();
  const subscribe = useCallback((onChange: () => void) => store.subscribe(onChange), [store]);
  // getSnapshot must not allocate: it returns the slice itself, and the
  // reducer's `default: return state` preserves identity for unrelated
  // actions, so React's Object.is check is stable and cannot loop. Note the
  // boundary — all four settings actions spread into a new object, so any
  // settings action re-renders consumers even when no value changed. That
  // matches the previous useSelector behavior exactly; it is not a regression,
  // and narrowing it is out of scope here.
  const getSettingsSnapshot = useCallback(() => (store.getState() as AppState).settings, [store]);
  return useSyncExternalStore(subscribe, getSettingsSnapshot, getSettingsSnapshot) as Settings;
}
